const ENERGY_LABEL_LEADS = {
  title: 'Energy Label Leads',
  description: 'Overview of leads for energy labeling',
  searchPlaceholder: 'Search leads …',
  emptyState: 'No leads found.',
  importButton: 'Import Excel',
  importError: 'Could not import the file.',
  importEmpty: 'The file contained no rows.',
  importSummary: 'Imported {imported} of {received} rows.',
  importRejected: 'Rejected rows:',
  importRow: 'Row {row}',
  importErrors: {
    required: '{field} is missing',
    invalid: '{field} is not valid',
    // more precise than the generic wording, worth its own key
    organizationNumber_invalid: 'Organization number must be 9 digits',
  },
  importFields: {
    contactPerson: 'Contact person',
    organizationNumber: 'Organization number',
    contactEmail: 'Email',
    contactPhone: 'Phone',
    companyName: 'Company name',
  },
  assignToMe: 'Assign to myself',
  assignToMeNeedsEnovaGuid:
    'Your user needs an Enova personal GUID to take a lead.',
  backToList: 'Back to leads',
  unassigned: 'Unassigned',
  loadError: 'Could not load leads.',
  assignError: 'Could not assign the lead.',
  notFound: 'Lead not found.',
  importNotImplemented: 'Excel import is not implemented yet.',
  commentSaveError: 'Could not save the note.',
  commentSaved: 'Note saved.',
  daysAgo: '{count} days ago',
  missingAddress: 'Missing address',
  deleteConfirmTitle: 'Delete lead',
  deleteConfirmMessage:
    'Are you sure you want to delete this lead? This cannot be undone.',
  deleteError: 'Could not delete the lead.',
  archiveError: 'Could not archive the lead.',
  blockError: 'Could not block the lead.',
  blockNotYours: 'Only the inspector this lead is assigned to can block it.',
  blockWrongStatus:
    'A lead can only be blocked while the inspection is under way.',
  archiveConfirmTitle: 'Archive lead',
  archiveConfirmMessage:
    'Are you sure you want to archive this lead? It will be marked as archived and leave the open cases.',
  archiveNotAllowed: 'A completed lead cannot be archived.',
  assignWrongStatus:
    'A lead can only be assigned while its status is Form completed, Assigned to advisor, Blocked or Missing information.',
  actions: {
    archive: 'Archive',
    block: 'Block',
    unblock: 'Unblock',
  },
  lifecycle: {
    title: 'Lifecycle',
    steps: {
      imported: 'Lead imported',
      created: 'Created — form sent to customer',
      formCompleted: 'Customer done — ready for inspector',
      inspectionStarted: 'Assigned to advisor',
      inspectionCompleted: 'Inspector finished',
      completed: 'Energy certificate issued',
    },
  },
  building: {
    title: 'Building',
    buildingNumber: 'Building number',
    buildingType: 'Building type',
    none: 'No building is linked to the lead yet.',
    open: 'Open the building',
  },
  energyLabel: {
    title: 'Energy label',
    grade: 'Energy grade',
    issued: 'Issued',
    openCertificate: 'Open the certificate',
    complete: 'Complete lead',
    completeHelp:
      'Sets the lead to completed and emails the energy certificate to the contact person',
    completeError: 'Could not complete the lead.',
  },
  inspection: {
    title: 'Inspection',
    name: 'Name',
    date: 'Inspection date',
    readSubmitted: 'Read submitted data',
    open: 'Open the inspection',
    create: 'Create building inspection',
    createAgain: 'Create a new building inspection',
    noLead: 'The lead could not be loaded.',
    fixErrorsFirst: 'Fix the {count} errors in the checks above first.',
    createHint:
      'The inspection is set up from the form above, with standard values where the customer gave none.',
    created: 'The building inspection was created.',
    noBuilding: 'Link a building to the lead first.',
    noSubmission: 'No submitted form is linked to the lead yet.',
    createError: 'Could not create the building inspection.',
    exists: 'A building inspection for this lead was just created.',
    filledCount: '{count} values were filled with standard values.',
  },
  filters: {
    allOpen: 'All open',
    waitingCustomer: 'Waiting on customer',
    readyForInspector: 'Ready for inspector',
    mine: 'My cases',
    completed: 'Completed',
    stopped: 'Stopped',
    archived: 'Archived',
  },
  columns: {
    contactPerson: 'Contact person',
    organizationNumber: 'Organization number',
    address: 'Address',
    status: 'Status',
    consultant: 'Consultant',
    created: 'Created',
    actions: 'Action',
  },
  reminder: {
    send: 'Send reminder',
    sent: 'Reminder sent',
    help: 'Resends the form link with the same deadline',
    noDeadline:
      'This lead has no form deadline yet — send the first notification first',
    error: 'Could not send the reminder.',
  },
  status: {
    imported: 'Imported',
    created: 'Created',
    formCompleted: 'Form completed',
    inspectionStarted: 'Assigned to advisor',
    inspectionCompleted: 'Inspection completed',
    completed: 'Energy label issued',
    blocked: 'Blocked',
    notEnoughInformation: 'Missing information',
    cancelled: 'Archived',
    unknown: 'Unknown',
  },
  detail: {
    companyName: 'Company name',
    internalComment: 'Internal note',
    internalCommentPlaceholder: 'Only visible to us, never to the customer',
    contactSection: 'Contact details',
    contactPerson: 'Contact person',
    phone: 'Phone',
    email: 'Email',
    companySection: 'Company',
    organizationNumber: 'Organization number',
    streetName: 'Street name',
    houseNumber: 'House number',
    houseLetter: 'Letter',
    postalCode: 'Postal code',
    city: 'City',
    mpidsSection: 'Metering point IDs',
    noMpids: 'No metering point IDs registered',
    status: 'Status',
    formSection: 'Customer form',
    formLink: 'Link to send the customer',
    copyLink: 'Copy link',
    linkCopied: 'Link copied.',
    formStatus: 'Form status',
    formNotStarted: 'Not opened yet',
    formDraft: 'In progress',
    formSubmitted: 'Submitted',
  },
  submissionView: {
    title: 'Submitted form',
    open: 'Read submitted data',
    draft: 'Draft',
    backToLead: 'Back to the lead',
    notStarted: 'The customer has not started the form yet.',
    loadError: 'Could not load the form.',
    item: 'System {number}',
    inactive: 'not in use',
    hiddenRows: '{count} fields do not apply and are empty',
    notApplicableEmpty: 'Does not apply to this building. No values given.',
    status: {
      DRAFT: 'Draft',
      SUBMITTED: 'Submitted',
      IN_REVIEW: 'In review',
      COMPLETED: 'Completed',
    },
    meta: {
      started: 'Started',
      lastSaved: 'Last saved',
      submitted: 'Submitted',
    },
    fields: {
      street: 'Address',
      postalCode: 'Postal code',
      city: 'City',
      gnr: 'Farm number (gnr)',
      bnr: 'Title number (bnr)',
      latitude: 'Latitude',
      longitude: 'Longitude',
      confirmedByCustomer: 'Building confirmed by the customer',
      buildingNumber: 'Building number',
      registryType: 'Building type in the registry',
      districtUseUnknown: "Doesn't know what the district heating is used for",
      goodFaith: 'Confirmed in good faith',
      termsAccepted: 'Terms accepted',
      confirmedAt: 'Confirmed',
      respondentEmail: 'Email of the person who answered',
      knowsBuilding: 'Knows the building technically',
      dataQuality: 'Data quality',
      completeness: 'Completed',
      anySource: 'At least one energy source chosen',
      anyHeating: 'At least one heating solution chosen',
      category: 'Building category',
      buildYear: 'Build year',
      floors: 'Number of floors',
    },
    tiers: {
      required: 'Required',
      important: 'Important',
      missing: 'Missing',
      defaults: 'Default value',
      notApplicable: 'Not applicable',
      complete: 'Complete',
    },
    readiness: {
      title: 'Data for the inspection',
      ready: 'All required fields are filled in',
      notReady: '{count} required fields not filled in',
      coverage: 'Data coverage',
      required: 'Required',
      important: 'Important for the calculation',
      windowShare: 'Window share of heated area',
      mismatches: 'Mismatches with the lead',
      missingRequired: 'Required fields the customer has not filled in',
      missingImportant: 'Missing, calculated with default values',
      nothingMissing: 'Nothing is missing.',
    },
    advisor: {
      title: 'Message to the energy advisor',
      count: '{count} from the customer',
    },
    checks: {
      title: 'Checks',
      none: 'The answers hang together.',
      allGood: 'Nothing found',
      stepCount: '{count} to check',
      severity: { error: 'Error', warning: 'Check', info: 'Note' },
      counts: {
        error: '{count} errors',
        warning: '{count} to check',
        info: '{count} notes',
      },
      messages: {
        outsideNorway: 'The coordinates are outside Norway.',
        farFromRegistryPoint:
          "The pin is {metres} m from the building's point in the registry.",
        locationNotConfirmed:
          'The customer has not confirmed the location on the map.',
        registry: {
          details:
            'The registry details could not be fetched. Area and floors are not checked against the registry.',
          notFound: 'The building was not found in the registry.',
          upstream:
            'The registry did not answer when the building was looked up.',
          timeout: 'The registry lookup took too long.',
        },
        registryAddressMismatch:
          'The address in the registry differs from the one the customer chose.',
        categoryDiffersFromRegistry:
          'The category differs from what the building type in the registry maps to.',
        subcategoryDiffersFromRegistry:
          'The subcategory differs from what the registry suggests.',
        areaDiffersFromRegistry:
          'Differs {percent} % from the usable area in the registry ({registry} m²).',
        floorsDifferFromRegistry:
          'The registry has {registry} floors recorded.',
        prefilledEstimate:
          'Prefilled as an estimate from the map, not measured or given.',
        prefilledDefault: 'Prefilled with a default value.',
        yearInFuture: 'The year is in the future.',
        noBuildYear:
          'No build year. The inspection cannot size the heating (it comes out at 0 kW) or fill U-values, air leakage, window g-values and ventilation from standard values. Add the build year before creating the inspection.',
        buildYearVeryOld:
          'Build year before 1800. Check that it is not a typo.',
        heatedAboveUsable:
          'Heated area ({heated} m²) is larger than the usable area ({usable} m²).',
        heatedMuchBelowUsable: 'Only {percent} % of the usable area is heated.',
        usableAboveFootprintTimesFloors:
          'The usable area is larger than footprint × floors ({max} m²).',
        usableBelowFootprint:
          'The usable area is below half the footprint ({footprint} m²).',
        manyFloors: 'More than 40 floors. Check that it is not a typo.',
        floorHeightUnusual: 'The floor height is outside the usual (2.2–6 m).',
        insulationUnusual: 'The insulation is thicker than 500 mm.',
        windowsAboveHeatedArea:
          'The window area is larger than the whole heated area ({share} %).',
        windowShareAboveTek17:
          'The windows are {share} % of the heated area. TEK17 allows at most {max} %.',
        orientationSumDiffers:
          'The sum per facade ({sum} m²) does not match the total ({total} m²).',
        installedBeforeBuilt:
          'Installed {year}, before the building was finished ({buildYear}).',
        annualUseAboveFullPower:
          'The annual use is more than full power all year gives ({max} kWh).',
        powerHighForArea:
          'The power comes to {wPerM2} W per m² of heated area, which is high.',
        solarYieldUnusual:
          'Gives {yield} kWh per kWp a year. Usual in Norway is 600–1100.',
        solarDensityUnusual:
          '{kwpPerM2} kWp per m² of panel. Usual is 0.15–0.23.',
        sharesNotHundred: 'The shares add up to {sum} %, not 100 %.',
        centralWithoutSource:
          'Water-based heating is chosen, but no energy source can heat the water.',
        districtWithoutDistribution:
          'District heating is chosen, but no water-based heating in the building.',
        systemCountDiffers: '{count} systems given, but {found} are filled in.',
        recoveryOnExhaustOnly:
          'Mechanical exhaust usually has no heat recovery.',
        recoveryVeryHigh: 'Heat recovery above 90 % is unusually high.',
        offAboveOperating:
          'The airflow outside operating hours is larger than during them.',
        airflowUnusual: '{perM2} m³/h per m² is outside the usual (0.3–20).',
      },
    },
    comparison: {
      title: 'Lead against form',
      lead: 'Lead and building',
      form: 'Form',
      allMatch: 'No mismatches',
      mismatchCount: '{count} mismatches',
      status: {
        match: 'Match',
        mismatch: 'Mismatch',
        missingLead: 'Only in the form',
        missingForm: 'Missing in the form',
      },
    },
  },
  loginLog: {
    title: 'Login log',
    description:
      'Every step when the customer logs in to the form with an SMS code. Deleted automatically after 90 days.',
    empty: 'Nobody has tried to log in to this lead yet.',
    loadError: 'Could not load the login log.',
    refresh: 'Refresh',
    columns: {
      time: 'Time',
      step: 'Step',
      outcome: 'Outcome',
      phone: 'Phone',
      ip: 'IP address',
    },
    steps: {
      requestCode: 'Asked for a code',
      verify: 'Entered the code',
      logout: 'Logged out',
    },
    outcomes: {
      sent: 'SMS sent',
      cooldown: 'Not sent, asked again too soon',
      smsFailed: 'SMS failed',
      invalidPhone: 'Invalid phone number',
      unknownLead: 'Unknown lead',
      closedLead: 'The lead is closed',
      noBuilding: 'The lead has no building',
      invalidFormat: 'The code was not six digits',
      wrongCode: 'Wrong code',
      codeExpired: 'The code had expired',
      tooManyAttempts: 'Too many attempts',
      noCode: 'No active code',
      buildingIncomplete: 'The building lacks data for the form',
      success: 'Logged in',
    },
    attemptsLeft: '{count} attempts left',
  },
};

export default ENERGY_LABEL_LEADS;
